import { openRequestDetailFromLink } from '../utils/openRequestLink.js';

export class Notification {
    constructor(options = {}) {
        this.wrapper = options.wrapper || document.querySelector('.notifications');

        if (!this.wrapper) {
            return;
        }

        this.ajaxUrl = window.docuswiftPortal?.ajax_url || '/wp-admin/admin-ajax.php';
        this.panels = [];
    }

    init() {
        if (!this.wrapper) {
            return;
        }

        this.buildPanels();
        this.setupEventListeners();
        this.panels.forEach((panel) => this.updateUnreadIndicatorsFromDom(panel));

        return this;
    }

    /**
     * Each notification category (Messages / Actions / System) gets its own
     * trigger icon, badge and dropdown panel, matched by data-notif-category.
     */
    buildPanels() {
        const triggers = this.wrapper.querySelectorAll('.notif-trigger[data-notif-category]');

        this.panels = Array.from(triggers)
            .map((trigger) => {
                const category = trigger.dataset.notifCategory;
                const dropdown = this.wrapper.querySelector(`.notif-dropdown[data-notif-category="${category}"]`);

                return {
                    category,
                    trigger,
                    dropdown,
                    isOpen: false,
                    badge: trigger.querySelector('.notif-badge'),
                    quantity: dropdown ? dropdown.querySelector('.notif-items-header .quantity') : null,
                };
            })
            .filter((panel) => panel.dropdown);
    }

    setupEventListeners() {
        this.panels.forEach((panel) => {
            panel.trigger.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.togglePanel(panel);
            });

            panel.dropdown.addEventListener('click', async (e) => {
                const readAllTrigger = e.target.closest('.notif-items-header__read-all');
                if (readAllTrigger) {
                    e.preventDefault();
                    e.stopPropagation();
                    const ok = await this.markAllAsSeen(panel.category);
                    if (ok) this.markAllItemsAsReadUI(panel);
                    return;
                }

                const item = e.target.closest('.notif-item');
                if (!item) return;

                const id = item.getAttribute('notif-id');
                if (!id) return;

                const viewedTrigger = e.target.closest('.notif-item__icon');
                const link = item.dataset?.link;

                const ok = await this.markAsSeen(id);
                if (ok) this.markItemAsReadUI(panel, item);

                if (!viewedTrigger && link) {
                    if (!this.openRequestFromLink(link)) {
                        window.location.href = link;
                    }
                }
            });
        });

        document.addEventListener('click', () => {
            this.closeAllPanels();
        });
    }

    togglePanel(panel) {
        if (panel.isOpen) {
            this.closePanel(panel);
            return;
        }

        this.closeAllPanels();
        this.openPanel(panel);
    }

    openPanel(panel) {
        panel.dropdown.classList.add('is-open');
        panel.isOpen = true;
    }

    closePanel(panel) {
        panel.dropdown.classList.remove('is-open');
        panel.isOpen = false;
    }

    closeAllPanels() {
        this.panels.forEach((panel) => this.closePanel(panel));
    }

    /**
     * Opens a category's dropdown from an element outside the bell widget
     * itself (e.g. a dashboard stat card or "View all" link) — used so those
     * shortcuts reuse the exact same panel/read-state logic as the bell icons
     * instead of duplicating it.
     */
    openCategoryByName(category) {
        const panel = this.panels.find((p) => p.category === category);
        if (!panel) return false;

        this.closeAllPanels();
        this.openPanel(panel);
        panel.trigger.scrollIntoView({ block: 'nearest' });
        return true;
    }

    openRequestFromLink(link) {
        return openRequestDetailFromLink(link);
    }

    toggleBadge(panel, unreadCount = 0) {
        if (!panel?.badge) return;

        const count = Math.max(0, parseInt(unreadCount, 10) || 0);
        count > 0
            ? panel.badge.classList.remove('hidden')
            : panel.badge.classList.add('hidden');
        panel.badge.textContent = count > 99 ? '99+' : String(count);
    }

    setUnreadCount(panel, unreadCount = 0) {
        if (!panel?.quantity) return;
        panel.quantity.textContent = String(Math.max(0, parseInt(unreadCount, 10) || 0));
    }

    updateUnreadIndicators(panel, unreadCount = 0) {
        this.toggleBadge(panel, unreadCount);
        this.setUnreadCount(panel, unreadCount);
    }

    /**
     * Fetch fresh unread counts from the server and update every bell panel
     * without reloading the page. Called after actions that change notification
     * state (e.g. an Admin/ROI external-note response marks prior notifications
     * as read).
     */
    refreshCounts() {
        if (!this.wrapper) return Promise.resolve();

        const formData = new URLSearchParams();
        formData.append('action', 'docuswift_ajax_get_notification_counts');

        return fetch(this.ajaxUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            },
            body: formData.toString()
        })
        .then(response => response.json())
        .then(response => {
            if (response.success && response.data?.counts) {
                const counts              = response.data.counts;
                const unreadIds           = response.data.unread_ids || {};
                const respondedRequestIds = response.data.responded_request_ids || [];
                const hiddenRequestIds    = response.data.hidden_request_ids || [];
                this.panels.forEach((panel) => {
                    this.updateUnreadIndicators(panel, counts[panel.category] ?? 0);
                    this.syncUnreadItems(panel, unreadIds[panel.category] || [], respondedRequestIds, hiddenRequestIds);
                });
                this.syncDashboardPanels(counts, unreadIds, respondedRequestIds, hiddenRequestIds);
            }
        })
        .catch(error => {
            console.error('Error refreshing notification counts:', error);
        });
    }

    /**
     * Update the visual read/unread state of already-rendered notification
     * items so previously-red unread indicators become grey when the backend
     * marks them as read (e.g. via the external-note "responded" logic).
     * Also sync the "Responded" tag for items whose request has been marked
     * as responded.
     */
    syncUnreadItems(panel, unreadIds = [], respondedRequestIds = [], hiddenRequestIds = []) {
        if (!panel?.dropdown) return;

        const unreadSet      = new Set(unreadIds.map(String));
        const respondedSet   = new Set(respondedRequestIds.map(String));
        const hiddenSet      = new Set(hiddenRequestIds.map(String));

        panel.dropdown.querySelectorAll('.notif-item').forEach((item) => {
            const id        = item.getAttribute('notif-id');
            const requestId = item.getAttribute('data-request-id');
            if (!id) return;

            if (requestId && hiddenSet.has(requestId)) {
                item.classList.add('is-hidden');
            } else {
                item.classList.remove('is-hidden');
            }

            if (unreadSet.has(id)) {
                item.classList.remove('is-read');
                item.classList.add('is-unread');
            } else {
                item.classList.remove('is-unread');
                item.classList.add('is-read');
            }

            this.toggleRespondedTag(item, requestId && respondedSet.has(requestId) && !hiddenSet.has(requestId));
        });
    }

    /**
     * Add or remove the "Responded" tag on a single notification item so it
     * matches the backend state without requiring a page reload.
     */
    toggleRespondedTag(item, show) {
        const title = item.querySelector('.notif-item__title');
        if (!title) return;

        let tag = title.querySelector('.notif-item__tag--responded');
        if (show) {
            if (tag) return;
            tag = document.createElement('span');
            tag.className = 'notif-item__tag notif-item__tag--responded';
            tag.textContent = 'Responded';
            const firstTag = title.querySelector('.notif-item__tag');
            if (firstTag) {
                title.insertBefore(tag, firstTag);
            } else {
                title.appendChild(tag);
            }
        } else if (tag) {
            tag.remove();
        }
    }

    /**
     * Sync the dashboard side-panel notification lists (Message Alerts /
     * Action Required / System Notifications) with the latest backend state:
     * update stat-card counts, read/unread classes, and Responded tags.
     */
    syncDashboardPanels(counts, unreadIds, respondedRequestIds, hiddenRequestIds = []) {
        if (!counts) return;

        const respondedSet = new Set(respondedRequestIds.map(String));
        const hiddenSet    = new Set(hiddenRequestIds.map(String));

        Object.keys(counts).forEach((category) => {
            const list = document.querySelector(`.roi-alerts--${category}`);
            if (!list) return;

            const count     = counts[category] ?? 0;
            const unreadSet = new Set((unreadIds[category] || []).map(String));

            const statValue = document.querySelector(`.roi-stat-card[data-notif-category="${category}"] .roi-stat-card__value`);
            if (statValue) {
                statValue.textContent = String(count);
            }

            list.querySelectorAll('li[notif-id]').forEach((item) => {
                const id        = item.getAttribute('notif-id');
                const requestId = item.getAttribute('data-request-id');
                if (!id) return;

                if (requestId && hiddenSet.has(requestId)) {
                    item.classList.add('is-hidden');
                } else {
                    item.classList.remove('is-hidden');
                }

                if (unreadSet.has(id)) {
                    item.classList.remove('is-read');
                    item.classList.add('is-unread');
                } else {
                    item.classList.remove('is-unread');
                    item.classList.add('is-read');
                }

                this.toggleDashboardRespondedTag(item, requestId && respondedSet.has(requestId) && !hiddenSet.has(requestId));
            });
        });
    }

    /**
     * Add or remove the "Responded" tag on a dashboard side-panel item.
     */
    toggleDashboardRespondedTag(item, show) {
        const strong = item.querySelector('strong');
        if (!strong) return;

        let tag = strong.querySelector('.roi-notif-tag--responded');
        if (show) {
            if (tag) return;
            tag = document.createElement('span');
            tag.className = 'roi-notif-tag roi-notif-tag--responded';
            tag.textContent = 'Responded';
            const firstTag = strong.querySelector('.roi-notif-tag');
            if (firstTag) {
                strong.insertBefore(tag, firstTag);
            } else {
                strong.appendChild(tag);
            }
        } else if (tag) {
            tag.remove();
        }
    }

    markAsSeen(notification_id) {
        if (!notification_id) return Promise.resolve(false);

        const formData = new URLSearchParams();
        formData.append('action', 'docuswift_ajax_mark_as_read');
        formData.append('notification_id', notification_id);

        return fetch(this.ajaxUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            },
            body: formData.toString()
        })
        .then(response => response.json())
        .then(response => {
            if (response.success) {
                return true;
            } else {
                console.error('Error marking notifications as seen:', response.data?.message);
                return false;
            }
        })
        .catch(error => {
            console.error('Error marking notifications as seen:', error);
            return false;
        });
    }

    markItemAsReadUI(panel, item) {
        if (!item) return;
        item.classList.remove('is-unread');
        item.classList.add('is-read');
        const viewed = item.querySelector('.notif-item__icon');
        if (viewed) {
            viewed.setAttribute('title', 'Viewed');
        }
        this.updateUnreadIndicatorsFromDom(panel);
    }

    markAllAsSeen(category) {
        const formData = new URLSearchParams();
        formData.append('action', 'docuswift_ajax_mark_all_as_read');
        formData.append('category', category || '');

        return fetch(this.ajaxUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            },
            body: formData.toString()
        })
        .then(response => response.json())
        .then(response => {
            if (response.success) {
                return true;
            } else {
                console.error('Error marking all notifications as read:', response.data?.message);
                return false;
            }
        })
        .catch(error => {
            console.error('Error marking all notifications as read:', error);
            return false;
        });
    }

    markAllItemsAsReadUI(panel) {
        if (!panel?.dropdown) return;

        panel.dropdown.querySelectorAll('.notif-item.is-unread').forEach((item) => {
            this.markItemAsReadUI(panel, item);
        });
    }

    updateUnreadIndicatorsFromDom(panel) {
        const unread = panel.dropdown?.querySelectorAll('.notif-item.is-unread:not(.is-hidden)')?.length || 0;
        this.updateUnreadIndicators(panel, unread);
    }

    sendNotification(title, link, userId, status = 'unread', notifiedByEmail = 0, notificationType = 'in-app') {
        const formData = new URLSearchParams();

        formData.append('action', 'docuswift_send_notification');
        formData.append('title', title);
        formData.append('link', link);
        formData.append('user_id', userId);
        formData.append('status', status);
        formData.append('notified_by_email', notifiedByEmail);
        formData.append('notification_type', notificationType);

        // Nonce (if available)
        if (window.docuswiftPortal?.nonce) {
            formData.append('_ajax_nonce', window.docuswiftPortal.nonce);
        }

        fetch(this.ajaxUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            },
            body: formData.toString()
        })
        .then(res => res.json())
        .then(response => {
            if (response.success) {
                console.log('Notification sent successfully');
            } else {
                console.error('Error sending notification:', response.data || response.error);
            }
        })
        .catch(error => {
            console.error('Error sending notification:', error);
        });
    }
}
